import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ChevronRight,
  type LucideIcon,
  MapPin,
  Sparkles,
} from "lucide-react";
import Link from "next/link";
import { Button } from "../ui/button";
import { Card } from "../ui/card";

interface MarketAvailabilityProps {
  title?: string;
  description?: string;
  className?: string;
  showRequestAccess?: boolean;
  featureHighlights?: Array<{
    title: string;
    description: string;
    icon: LucideIcon;
  }>;
}

const DEFAULT_FEATURE_HIGHLIGHTS = [
  {
    title: "Real-Time Property Matches",
    description:
      "Monitor all properties that match your search criteria in one place",
    icon: Sparkles,
  },
  {
    title: "Instant Market Updates",
    description: "Get notified when new properties match your preferences",
    icon: MapPin,
  },
  {
    title: "Smart Property Insights",
    description: "View detailed analysis of each matching property",
    icon: ArrowRight,
  },
];

export function MarketAvailability({
  title = "Market Matches Available in Denver",
  description = "Monitor properties that match your search criteria, all in one place. Currently available in Denver, CO.",
  className,
  showRequestAccess = true,
  featureHighlights = DEFAULT_FEATURE_HIGHLIGHTS,
}: MarketAvailabilityProps) {
  return (
    <Card
      className={cn(
        "overflow-hidden bg-gradient-to-b from-background to-muted/20",
        className,
      )}
    >
      <div className="p-6 md:p-8 space-y-6">
        {/* Header */}
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-primary mb-4"
          >
            <MapPin className="w-4 h-4" />
            <span>Currently available in Denver, CO</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-2xl md:text-3xl font-semibold mb-3"
          >
            {title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-muted-foreground md:text-lg"
          >
            {description}
          </motion.p>
        </div>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {featureHighlights.map((highlight, index) => (
            <motion.div
              key={highlight.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 bg-background hover:bg-muted rounded-xl transition-all border border-border space-y-3"
            >
              <div className="bg-primary/10 rounded-lg p-2.5 w-fit">
                <highlight.icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">{highlight.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {highlight.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Update Location */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-6"
        >
          <Link href="/app/settings" className="block">
            <Button variant="outline" className="text-xs md:text-base">
              Update Home Search Location
              <ChevronRight className="w-4 h-4 md:ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </Card>
  );
}
